/**
 * @description Component-TradeForm
 */
import './index.less';

import { useDebounceEffect, usePrevious } from 'ahooks';
import React, { FC, useCallback, useMemo } from 'react';

import { AvailableBalancePosition } from '@/components/AvailableBalancePosition';
import { useMediaQueryDevice } from '@/components/MediaQuery';
import { FETCHING_STATUS } from '@/constants';
import { PAIR_PAGE_TYPE } from '@/constants/global';
import { TRADE_OVERLAY_TYPE, TRADE_TYPE } from '@/constants/trade';
import { useCombinedPairFromUrl } from '@/features/pair/hook';
import { resetFormByChainId, setTradeFormSide, setTradeFormType } from '@/features/trade/actions';
import {
  useLimitFormStatus,
  useMarketFormStateStatus,
  useScaleFormStateStatus,
  useTradeType,
} from '@/features/trade/hooks';
import { useTradeStore } from '@/features/trade/store';
import { useAppDispatch } from '@/hooks';
import { useChainId } from '@/hooks/web3/useChain';
import DepositNativeCard from '@/pages/components/Portfolio/Vault/AccountButtons/Deposit/DepositModal/DepositNativeCard';
import { Side } from '@synfutures/sdks-perp';
import { useLocation } from 'react-router-dom';
import ScaleLimitFormSimple from './ScaleLimitFormSimple';
import TradeFormSide from './TradeFormBase/TradeFormSide';
import TradeFormHeader from './TradeFormHeader';
import TradeFormTradeTypeTabs from './TradeFormTradeTypeTabs';
import TradeLimitForm from './TradeLimitForm';
import TradeMarketFormSimple from './TradeMarketFormSimple';

interface IPropTypes {
  children?: React.ReactNode;
}

const TradeForm: FC<IPropTypes> = function ({}) {
  const dispatch = useAppDispatch();
  const chainId = useChainId();
  const tradeType = useTradeType(chainId);
  const scaleLimitFormStatus = useScaleFormStateStatus(chainId);
  const marketFormStatus = useMarketFormStateStatus(chainId);
  const limitFormStatus = useLimitFormStatus(chainId);
  const { isMobile } = useMediaQueryDevice();
  const currentPair = useCombinedPairFromUrl(chainId);

  const tradeFromSideDisable = useMemo(() => {
    if (tradeType === TRADE_TYPE.MARKET) {
      return marketFormStatus === FETCHING_STATUS.FETCHING;
    }
    if (tradeType === TRADE_TYPE.LIMIT) {
      return limitFormStatus === FETCHING_STATUS.FETCHING;
    }
    if (tradeType === TRADE_TYPE.SCALE_LIMIT) {
      return scaleLimitFormStatus === FETCHING_STATUS.FETCHING;
    }
    return false;
  }, [tradeType, marketFormStatus, limitFormStatus, scaleLimitFormStatus]);

  const tradeTypePrev = usePrevious(tradeType);

  const closeDepositNativeInTrade = useCallback(() => {
    if (chainId) {
      if (tradeTypePrev === TRADE_TYPE.ADJUST_MARGIN) {
        dispatch(
          setTradeFormType({
            chainId,
            tradeType: TRADE_TYPE.ADJUST_MARGIN,
          }),
        );
      } else {
        dispatch(
          resetFormByChainId({
            chainId,
          }),
        );
      }
    }
  }, [chainId, dispatch, tradeTypePrev]);

  const { setShowTradeAreaOverlay } = useTradeStore();

  const location = useLocation();

  // navigate to earn page, show add liq overlay
  useDebounceEffect(
    () => {
      console.log(`TradeForm mount`, location.pathname);
      if (location.pathname.startsWith(`/${PAIR_PAGE_TYPE.EARN}`)) {
        setShowTradeAreaOverlay(TRADE_OVERLAY_TYPE.ADD_LIQ);
      }
    },
    [],
    { wait: 500 },
  );

  return (
    <div className="trade-area" style={{ position: 'relative' }}>
      {[TRADE_TYPE.MARKET, TRADE_TYPE.LIMIT, TRADE_TYPE.SCALE_LIMIT].includes(tradeType as TRADE_TYPE) && (
        <div className="syn-trade-form">
          <div className="syn-trade-form-content">
            <TradeFormHeader />
            <TradeFormTradeTypeTabs />
            {!isMobile && (
              <TradeFormSide
                chainId={chainId}
                disabled={tradeFromSideDisable}
                onSideChange={(key) => {
                  const tradeSide = Number(key) as Side;
                  chainId &&
                    dispatch(
                      setTradeFormSide({
                        chainId,
                        tradeSide,
                      }),
                    );
                }}
              />
            )}
            <AvailableBalancePosition currentPair={currentPair} />
            {tradeType === TRADE_TYPE.MARKET && <TradeMarketFormSimple />}
            {tradeType === TRADE_TYPE.LIMIT && <TradeLimitForm />}
            {tradeType === TRADE_TYPE.SCALE_LIMIT && <ScaleLimitFormSimple />}
          </div>
        </div>
      )}
      {tradeType === TRADE_TYPE.DEPOSIT_NATIVE && (
        <DepositNativeCard closeDepositNativeInTrade={closeDepositNativeInTrade} />
      )}
    </div>
  );
};

export default TradeForm;
